"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import type { FUSportsLivescoreEvent, SearchEntityResult } from "fansunited-data-layer";
import { cn } from "@/utilities/core/cn";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { asCompetitor, getResultScore } from "@/views/livescore/utils/livescore";
import type { LivescoreSport } from "@/views/livescore/utils/livescore";
import { MatchTimeColumn } from "@/views/livescore/components/match-row/MatchTimeColumn";
import { LivescoreOdds } from "@/views/livescore/components/match-row/LivescoreOdds";
import type { BookmakerOdds } from "@/features/odds/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { MatchTvIndicator } from "@/features/tv-listings/frontend/MatchTvIndicator";
import type { ResolvedListings } from "@/features/tv-listings/utils/resolveMatchListings";

/**
 * Derive AET/AP from the score-period key names. On the events endpoint
 * we don't have `winner.reason` (that's on event-by-id only), so we sniff
 * the score array: presence of an overtime period → aet, penalty/shootout → ap.
 * Mirrors the classification logic in views/sportEvent/utils/sportShape.ts.
 */
function derivePostRegulationCode(scores: readonly {
  key: string;
}[] | undefined): "aet" | "ap" | null {
  if (!scores?.length) return null;
  for (const s of scores) {
    const k = s.key.toLowerCase();
    if (k.includes("penalt") || k === "so" || k.startsWith("shootout")) return "ap";
  }
  for (const s of scores) {
    const k = s.key.toLowerCase();
    if (k.includes("overtime") || k.includes("extra_time") || k.startsWith("ot") || k === "et") return "aet";
  }
  return null;
}
interface Props {
  event: FUSportsLivescoreEvent;
  entities: Record<string, SearchEntityResult>;
  sport: LivescoreSport;
  /** First bookmaker's odds for this match; undefined when unavailable / geo-blocked / disabled. */
  odds?: BookmakerOdds;
  /** When false, the odds strip is not rendered regardless of `odds`. */
  showOdds?: boolean;
  /** Pre-resolved TV listings for this match (undefined → no broadcasters assigned). */
  tvListings?: ResolvedListings;
  /** When true, the TV slot is reserved even without broadcasters, so rows stay aligned. */
  reserveTvSlot?: boolean;
}

/**
 * Compact vertically-stacked match row for narrow contexts (post body).
 *
 * Layout:
 *   [time]  [logoA] Team A .... scoreA
 *           [logoB] Team B .... scoreB
 *   [odds strip, full width, football only]
 */
export function CompactMatchRow(t0) {
  const $ = _c(100);
  const {
    event,
    entities,
    sport,
    odds,
    showOdds: t1,
    tvListings,
    reserveTvSlot: t2
  } = t0;
  const showOdds = t1 === undefined ? true : t1;
  const reserveTvSlot = t2 === undefined ? false : t2;
  const nav = useNavigation();
  const {
    hasOdds
  } = useTenantSettings();
  const labels = useLabels();
  let t3;
  if ($[0] !== entities || $[1] !== event.competitorOne.id) {
    t3 = asCompetitor(entities, event.competitorOne.id);
    $[0] = entities;
    $[1] = event.competitorOne.id;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const teamOne = t3;
  let t4;
  if ($[3] !== entities || $[4] !== event.competitorTwo.id) {
    t4 = asCompetitor(entities, event.competitorTwo.id);
    $[3] = entities;
    $[4] = event.competitorTwo.id;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const teamTwo = t4;
  const nameOne = teamOne?.name ?? event.competitorOne.id;
  const nameTwo = teamTwo?.name ?? event.competitorTwo.id;
  const oneLogo = teamOne?.entityType === "athlete" ? teamOne.assets?.photo ?? teamOne.assets?.logo : teamOne?.assets?.logo;
  const twoLogo = teamTwo?.entityType === "athlete" ? teamTwo.assets?.photo ?? teamTwo.assets?.logo : teamTwo?.assets?.logo;
  let t5;
  if ($[6] !== event.status?.type) {
    t5 = event.status?.type?.toUpperCase();
    $[6] = event.status?.type;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const statusType = t5;
  const isLive = statusType === "LIVE";
  const isFinished = statusType === "FINISHED";
  const isActive = isLive || isFinished;
  let strOne;
  let t6;
  let total;
  if ($[8] !== event.score) {
    total = getResultScore(event.score);
    strOne = (total.competitorOne ?? "").trim();
    t6 = (total.competitorTwo ?? "").trim();
    $[8] = event.score;
    $[9] = strOne;
    $[10] = t6;
    $[11] = total;
  } else {
    strOne = $[9];
    t6 = $[10];
    total = $[11];
  }
  const strTwo = t6;
  const numC1 = strOne === "" ? NaN : Number(strOne);
  const numC2 = strTwo === "" ? NaN : Number(strTwo);
  const hasNumericScores = !isNaN(numC1) && !isNaN(numC2);
  const c1Won = isFinished && hasNumericScores && numC1 > numC2;
  const c2Won = isFinished && hasNumericScores && numC2 > numC1;
  const postRegCode = isFinished ? derivePostRegulationCode(event.score) : null;
  const postRegLabel = postRegCode ? labels.matchPage.status[postRegCode] : null;
  let t7;
  if ($[12] !== event.id || $[13] !== nav || $[14] !== sport) {
    t7 = sport ? nav.sportEvent(sport, event.id) : undefined;
    $[12] = event.id;
    $[13] = nav;
    $[14] = sport;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const href = t7;
  const liveBorderClass = isLive ? sport === "football" ? "border-l-2 border-l-green-500" : "border-l-2 border-l-orange-500" : "";
  let t8;
  if ($[16] !== isLive || $[17] !== sport) {
    t8 = (won, lost) => cn("w-7 text-right text-sm tabular-nums shrink-0", isLive ? cn("font-bold", sport === "football" ? "text-green-500" : "text-orange-400") : won ? "font-bold text-foreground" : lost ? "text-muted-foreground" : "text-foreground");
    $[16] = isLive;
    $[17] = sport;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const teamScoreClass = t8;
  const teamNameClass = _temp;
  const rowLogoKind = sport === "tennis" ? "player" : "team";
  let t9;
  if ($[19] !== event || $[20] !== postRegLabel || $[21] !== sport) {
    t9 = <div className="w-16 shrink-0 flex items-center justify-center"><MatchTimeColumn event={event} sport={sport} showDate={true} postRegLabel={postRegLabel} /></div>;
    $[19] = event;
    $[20] = postRegLabel;
    $[21] = sport;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = teamOne?.country?.code;
  const t11 = teamOne?.country?.flag;
  const t12 = teamOne?.metadata?.branding?.primaryColor;
  const t13 = teamOne?.metadata?.branding?.secondaryColor;
  let t14;
  if ($[23] !== nameOne || $[24] !== oneLogo || $[25] !== rowLogoKind || $[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t13) {
    t14 = <TeamLogo src={oneLogo} alt={nameOne} size="sm" kind={rowLogoKind} countryCode={t10} countryFlag={t11} primaryColor={t12} secondaryColor={t13} />;
    $[23] = nameOne;
    $[24] = oneLogo;
    $[25] = rowLogoKind;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== c1Won || $[32] !== c2Won) {
    t15 = teamNameClass(c1Won, c2Won);
    $[31] = c1Won;
    $[32] = c2Won;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== nameOne || $[35] !== t15) {
    t16 = <span className={t15}>{nameOne}</span>;
    $[34] = nameOne;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== c1Won || $[38] !== c2Won || $[39] !== teamScoreClass) {
    t17 = teamScoreClass(c1Won, c2Won);
    $[37] = c1Won;
    $[38] = c2Won;
    $[39] = teamScoreClass;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const t18 = isActive ? total.competitorOne ?? "-" : "";
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <span className={t17}>{t18}</span>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t14 || $[45] !== t16 || $[46] !== t19) {
    t20 = <div className="flex items-center gap-2">{t14}{t16}{t19}</div>;
    $[44] = t14;
    $[45] = t16;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const t21 = teamTwo?.country?.code;
  const t22 = teamTwo?.country?.flag;
  const t23 = teamTwo?.metadata?.branding?.primaryColor;
  const t24 = teamTwo?.metadata?.branding?.secondaryColor;
  let t25;
  if ($[48] !== nameTwo || $[49] !== rowLogoKind || $[50] !== t21 || $[51] !== t22 || $[52] !== t23 || $[53] !== t24 || $[54] !== twoLogo) {
    t25 = <TeamLogo src={twoLogo} alt={nameTwo} size="sm" kind={rowLogoKind} countryCode={t21} countryFlag={t22} primaryColor={t23} secondaryColor={t24} />;
    $[48] = nameTwo;
    $[49] = rowLogoKind;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
    $[54] = twoLogo;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== c1Won || $[57] !== c2Won) {
    t26 = teamNameClass(c2Won, c1Won);
    $[56] = c1Won;
    $[57] = c2Won;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== nameTwo || $[60] !== t26) {
    t27 = <span className={t26}>{nameTwo}</span>;
    $[59] = nameTwo;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== c1Won || $[63] !== c2Won || $[64] !== teamScoreClass) {
    t28 = teamScoreClass(c2Won, c1Won);
    $[62] = c1Won;
    $[63] = c2Won;
    $[64] = teamScoreClass;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  const t29 = isActive ? total.competitorTwo ?? "-" : "";
  let t30;
  if ($[66] !== t28 || $[67] !== t29) {
    t30 = <span className={t28}>{t29}</span>;
    $[66] = t28;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t25 || $[70] !== t27 || $[71] !== t30) {
    t31 = <div className="flex items-center gap-2">{t25}{t27}{t30}</div>;
    $[69] = t25;
    $[70] = t27;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== t20 || $[74] !== t31) {
    t32 = <div className="flex-1 min-w-0 space-y-1.5">{t20}{t31}</div>;
    $[73] = t20;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== event.id || $[77] !== labels || $[78] !== reserveTvSlot || $[79] !== tvListings) {
    t33 = reserveTvSlot && <div className="shrink-0 w-5 flex items-center justify-center">{tvListings && <MatchTvIndicator matchNativeId={event.id} labels={labels} hasBroadcasters={true} resolved={tvListings} />}</div>;
    $[76] = event.id;
    $[77] = labels;
    $[78] = reserveTvSlot;
    $[79] = tvListings;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== t32 || $[82] !== t33 || $[83] !== t9) {
    t34 = <div className="flex items-stretch gap-2 px-2 py-2">{t9}{t32}{t33}</div>;
    $[81] = t32;
    $[82] = t33;
    $[83] = t9;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  const matchContent = t34;
  let t35;
  if ($[85] !== liveBorderClass) {
    t35 = cn("hover:bg-muted/40 transition-colors", liveBorderClass);
    $[85] = liveBorderClass;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== href || $[88] !== matchContent) {
    t36 = href ? <Link href={href} prefetch={false} className="block">{matchContent}</Link> : matchContent;
    $[87] = href;
    $[88] = matchContent;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== event.id || $[91] !== event.startTime || $[92] !== hasOdds || $[93] !== odds || $[94] !== showOdds) {
    t37 = showOdds && hasOdds && odds && <div className="px-2 pb-2"><LivescoreOdds bookmakerOdds={odds} isMobile={true} matchId={event.id} kickoffAt={event.startTime ?? undefined} /></div>;
    $[90] = event.id;
    $[91] = event.startTime;
    $[92] = hasOdds;
    $[93] = odds;
    $[94] = showOdds;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t35 || $[97] !== t36 || $[98] !== t37) {
    t38 = <div className={t35}>{t36}{t37}</div>;
    $[96] = t35;
    $[97] = t36;
    $[98] = t37;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  return t38;
}
function _temp(won_0, lost_0) {
  return cn("flex-1 min-w-0 text-sm truncate", won_0 ? "font-bold" : lost_0 ? "text-muted-foreground" : undefined);
}